import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useClientAuth } from '../../contexts/ClientAuthContext';
import { useOnboarding, useSaveOnboarding } from '../../hooks/useOnboarding';
import {
  FileText, Home, PartyPopper, ArrowRight, CheckCircle2, Circle,
  Loader2, MessageCircle, UserCheck, Banknote, Building2,
  Sparkles, PencilLine, Pencil, Plus, Trash2, ShieldCheck,
} from 'lucide-react';
import { useProperties } from '../../hooks/useManagement';

type StepState = 'done' | 'current' | 'todo';

interface ConnectStatus { connected: boolean; charges_enabled: boolean }

export default function ClientDashboardPage() {
  const { user } = useClientAuth();
  const { t } = useTranslation();
  const { data, isLoading } = useOnboarding();
  const { data: properties } = useProperties();
  const save = useSaveOnboarding();

  // Stripe Connect status — drives the final "Conecta tus cobros" step.
  const { data: connect } = useQuery({
    queryKey: ['connect-status'],
    queryFn: async (): Promise<ConnectStatus> => {
      const res = await fetch('/api/portal/connect/status', { credentials: 'same-origin' });
      if (!res.ok) return { connected: false, charges_enabled: false };
      return res.json();
    },
  });

  if (isLoading || !data) {
    return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-teal-500" size={32} /></div>;
  }

  const setupMode = data.profile.setupMode;
  const chooseSetupMode = (mode: 'self' | 'done_for_you') => save.mutate({ setupMode: mode });

  const signed = data.agreement.signed;
  const paid = data.payment.status === 'paid';
  const submitted = data.profile.status === 'submitted';
  const chargesEnabled = connect?.charges_enabled ?? false;
  const hasProperties = (properties?.length ?? 0) > 0;
  const allDone = signed && paid && submitted && hasProperties && chargesEnabled;

  // Funnel: account ✓ → sign agreement & pay → business details → properties → submit → connect payouts
  const steps: { key: string; state: StepState; icon: typeof FileText; to: string; title: string; desc: string; cta: string }[] = [
    {
      key: 'account', state: 'done', icon: UserCheck, to: '/portal/settings',
      title: t('portal.home.step.account.title'), desc: t('portal.home.step.account.desc'), cta: t('portal.home.step.account.cta'),
    },
    {
      key: 'agreementPay', state: (signed && paid) ? 'done' : 'current', icon: FileText, to: '/portal/agreement',
      title: t('portal.home.step.agreementPay.title', 'Acuerdo y pago'),
      desc: t('portal.home.step.agreementPay.desc', 'Firma tu acuerdo y elige tu plan en un solo paso.'),
      cta: signed ? t('portal.home.step.payment.cta', 'Elegir plan y pagar') : t('portal.home.step.agreement.cta', 'Firmar y continuar'),
    },
    {
      key: 'businessDetails', state: submitted ? 'done' : (signed && paid) ? 'current' : 'todo', icon: Home, to: '/portal/onboarding',
      title: t('portal.home.step.businessDetails.title', 'Datos del negocio'),
      desc: t('portal.home.step.businessDetails.desc', 'Tus datos fiscales, marca, idiomas y preferencias.'),
      cta: t('portal.home.step.businessDetails.cta', 'Completar datos'),
    },
    {
      key: 'properties', state: hasProperties ? 'done' : (signed && paid) ? 'current' : 'todo', icon: Building2, to: '/portal/manage/properties',
      title: t('portal.home.step.properties.title', 'Tus alojamientos'),
      desc: t('portal.home.step.properties.desc', 'Añade tus alojamientos con fotos, precios y comodidades.'),
      cta: t('portal.home.step.properties.cta', 'Añadir alojamientos'),
    },
    {
      key: 'payouts',
      // Decoupled from `submitted`: owners can connect Stripe any time after paying.
      state: chargesEnabled ? 'done' : (signed && paid) ? 'current' : 'todo',
      icon: Banknote, to: '/portal/manage/payouts',
      title: t('portal.home.step.payouts.title', 'Conecta tus cobros (Stripe)'),
      desc: t('portal.home.step.payouts.desc', 'Conecta Stripe para recibir los pagos de tus reservas (0% comisión).'),
      cta: t('portal.home.step.payouts.cta', 'Conectar Stripe'),
    },
  ];

  const doneCount = steps.filter(s => s.state === 'done').length;
  const pct = Math.round((doneCount / steps.length) * 100);
  const nextStep = steps.find(s => s.state === 'current');

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      {/* Welcome */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 to-teal-800 text-white p-6 sm:p-8">
        <div className="relative z-10">
          <h1 className="text-2xl sm:text-3xl font-bold">
            {t('portal.home.hello', { name: user?.name?.split(' ')[0] })} 👋
          </h1>
          <p className="text-teal-50/90 mt-2 max-w-xl">
            {allDone ? t('portal.home.welcomeDone') : t('portal.home.welcome')}
          </p>
        </div>
        <Home className="absolute -right-4 -bottom-4 text-white/10" size={140} />
      </div>

      {/* Setup mode: choose at the very start how the account gets set up. */}
      {!setupMode ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
          <h2 className="text-lg font-bold text-slate-900">{t('portal.home.setup.title', '¿Cómo quieres configurar tu cuenta?')}</h2>
          <p className="text-sm text-slate-500 mt-1">{t('portal.home.setup.subtitle', 'Elige cómo prefieres empezar. Puedes cambiarlo cuando quieras.')}</p>
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {/* Self-service */}
            <button
              type="button"
              onClick={() => chooseSetupMode('self')}
              disabled={save.isPending}
              className="text-left rounded-xl border border-slate-200 p-5 hover:border-teal-400 hover:bg-teal-50/40 transition-colors disabled:opacity-60"
            >
              <PencilLine size={24} className="text-teal-600" />
              <p className="font-semibold text-slate-900 mt-3">{t('portal.home.setup.self.title', 'Configúralo tú mismo')}</p>
              <p className="text-sm text-slate-500 mt-1">{t('portal.home.setup.self.desc', 'Añade tus alojamientos, fotos y precios a tu ritmo desde el panel.')}</p>
            </button>
            {/* Done for you */}
            <button
              type="button"
              onClick={() => chooseSetupMode('done_for_you')}
              disabled={save.isPending}
              className="text-left relative rounded-xl border-2 border-teal-300 bg-teal-50/50 p-5 hover:bg-teal-50 transition-colors disabled:opacity-60"
            >
              <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-teal-600 text-white">
                {t('portal.home.setup.freeBadge', 'Gratis')}
              </span>
              <Sparkles size={24} className="text-teal-600" />
              <p className="font-semibold text-slate-900 mt-3">{t('portal.home.setup.dfy.title', 'Lo configuramos por ti — gratis')}</p>
              <p className="text-sm text-slate-600 mt-1">{t('portal.home.setup.dfy.desc', 'Danos los enlaces de tus anuncios de Booking/Airbnb y nuestro equipo monta tus alojamientos, fotos, precios y descripciones por ti. Incluido en tu plan.')}</p>
            </button>
          </div>
          {save.isPending && (
            <p className="text-xs text-slate-400 mt-3 flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> {t('portal.home.setup.saving', 'Guardando…')}</p>
          )}
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
              {setupMode === 'done_for_you' ? <Sparkles size={18} /> : <PencilLine size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900">
                {setupMode === 'done_for_you'
                  ? t('portal.home.setup.chosenDfy', 'Configuración: lo hacemos nosotros')
                  : t('portal.home.setup.chosenSelf', 'Configuración: lo haces tú mismo')}
              </p>
              <p className="text-sm text-slate-500 mt-0.5">
                {setupMode === 'done_for_you'
                  ? t('portal.home.setup.chosenDfyDesc', 'Pega abajo los enlaces de tus anuncios y nuestro equipo se encarga del resto.')
                  : t('portal.home.setup.chosenSelfDesc', 'Completa tus datos y alojamientos desde el panel a tu ritmo.')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => save.mutate({ setupMode: null })}
              disabled={save.isPending}
              className="shrink-0 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-teal-600 disabled:opacity-50"
            >
              <Pencil size={12} /> {t('portal.home.setup.change', 'Cambiar')}
            </button>
          </div>

          {/* Done-for-you: capture the existing-listing URLs so the team can copy from them. */}
          {setupMode === 'done_for_you' && (
            <DoneForYouLinks
              initialUrls={
                data.profile.data?.listingUrls?.length
                  ? data.profile.data.listingUrls
                  : ([data.profile.listingBooking, data.profile.listingAirbnb, data.profile.listingVrbo, data.profile.currentWebsite].filter(Boolean) as string[])
              }
              onSave={(urls) => save.mutate({ data: { ...(data.profile.data ?? {}), listingUrls: urls } })}
              saving={save.isPending}
            />
          )}
        </div>
      )}

      {/* Progress */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-slate-900">{t('portal.home.progressTitle')}</h2>
          <span className="text-sm text-slate-500">{t('portal.home.progressCount', { done: doneCount, total: steps.length })}</span>
        </div>
        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-teal-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-3">
        {steps.map(step => (
          <Link
            key={step.key}
            to={step.to}
            className={`flex items-center gap-4 rounded-xl border p-4 transition-colors ${
              step.state === 'current'
                ? 'border-teal-300 bg-teal-50/60 hover:bg-teal-50'
                : 'border-slate-200 bg-white hover:bg-slate-50'
            }`}
          >
            <div className="shrink-0">
              {step.state === 'done'
                ? <CheckCircle2 size={26} className="text-emerald-500" />
                : step.state === 'current'
                ? <step.icon size={26} className="text-teal-600" />
                : <Circle size={26} className="text-slate-300" />}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`font-medium ${step.state === 'todo' ? 'text-slate-400' : 'text-slate-900'}`}>{step.title}</p>
              <p className="text-sm text-slate-500 truncate">{step.desc}</p>
            </div>
            {step.state !== 'done' && (
              <span className={`shrink-0 text-sm font-medium flex items-center gap-1 ${step.state === 'current' ? 'text-teal-600' : 'text-slate-400'}`}>
                {step.cta} <ArrowRight size={15} />
              </span>
            )}
          </Link>
        ))}
      </div>

      {/* Optional: SES.Hospedajes (Spanish traveler registration) — does not
          count toward setup completion; only relevant for ES accommodations. */}
      <Link to="/portal/manage/ses"
        className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4 hover:bg-slate-50 transition-colors">
        <ShieldCheck size={26} className="shrink-0 text-slate-400" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 flex items-center gap-2">
            {t('portal.home.step.ses.title', 'Registro de viajeros (SES.Hospedajes)')}
            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">{t('portal.home.optional', 'Opcional')}</span>
          </p>
          <p className="text-sm text-slate-500 truncate">{t('portal.home.step.ses.desc', 'Obligatorio en España (RD 933/2021). Configura tu cuenta SES y envía el parte de viajeros.')}</p>
        </div>
        <span className="shrink-0 text-sm font-medium flex items-center gap-1 text-slate-400">
          {t('portal.home.step.ses.cta', 'Configurar')} <ArrowRight size={15} />
        </span>
      </Link>

      {/* Next action / done */}
      {allDone ? (
        <div className="flex items-start gap-3 rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800">
          <PartyPopper size={22} className="shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">{t('portal.home.doneTitle')}</p>
            <p className="text-sm text-emerald-700 mt-0.5">{t('portal.home.doneDesc')}</p>
          </div>
        </div>
      ) : nextStep && (
        <Link to={nextStep.to}
          className="flex items-center justify-center gap-2 w-full py-3.5 px-4 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors">
          {nextStep.cta} <ArrowRight size={18} />
        </Link>
      )}

      {/* Help */}
      <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
        <MessageCircle size={20} className="text-teal-600 shrink-0" />
        <span>{t('portal.home.helpHint')}</span>
      </div>
    </div>
  );
}

// Done-for-you: capture the owner's existing-listing URLs inline so the Kanarix team
// can copy their properties/photos/prices from them. Reuses the onboarding listing_* fields.
function DoneForYouLinks({ initialUrls, onSave, saving }: {
  initialUrls: string[];
  onSave: (urls: string[]) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<string[]>(initialUrls.length ? initialUrls : ['']);
  const fieldCls = 'flex-1 min-w-0 px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm';

  const update = (i: number, v: string) => setUrls(u => u.map((x, j) => (j === i ? v : x)));
  const add = () => setUrls(u => [...u, '']);
  const remove = (i: number) => setUrls(u => {
    const next = u.filter((_, j) => j !== i);
    return next.length ? next : [''];
  });

  return (
    <div className="rounded-xl bg-teal-50/50 border border-teal-100 p-4 space-y-3">
      <p className="text-sm font-medium text-teal-900">{t('portal.home.setup.dfyLinksTitle', 'Pega los enlaces de tus anuncios')}</p>
      <p className="text-xs text-teal-800/80 -mt-2">{t('portal.home.setup.dfyLinksHint2', 'Añade un enlace por cada alojamiento (Booking, Airbnb, Vrbo o tu web). Copiaremos tus alojamientos, fotos, precios y descripciones. Nuestro equipo se encarga del resto.')}</p>
      <div className="space-y-2">
        {urls.map((url, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              value={url}
              placeholder={t('portal.home.setup.dfyLinkPlaceholder', 'https://booking.com/… o https://airbnb.com/…')}
              onChange={e => update(i, e.target.value)}
              className={fieldCls}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label={t('common.remove', 'Quitar')}
              className="shrink-0 p-2 text-slate-400 hover:text-rose-500"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-700 hover:text-teal-800"
      >
        <Plus size={15} /> {t('portal.home.setup.dfyAddLink', 'Añadir enlace')}
      </button>
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          onClick={() => onSave(urls.map(u => u.trim()).filter(Boolean))}
          disabled={saving}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
          {t('portal.home.setup.dfySave', 'Guardar enlaces')}
        </button>
        <Link to="/portal/onboarding" className="text-xs font-medium text-teal-700 hover:underline">
          {t('portal.home.setup.dfyMore', 'Más opciones en Datos del negocio')} →
        </Link>
      </div>
    </div>
  );
}
